import React, { useEffect } from "react"
import {
	useCalculateTotalCreditsCostFromTokenEstimations,
	useGetCreditsAvailable,
} from "../../hooks/useAI.js"
import useDebounce from "../../hooks/useDebounce.js"
import { estimateTokens } from "../../utils/estimateTokens.js"
import { loadGPTTokenizer } from "../../utils/getGPTTokens.js"

export const defaultAIState = {
	// Single product import
	aiOrders: [],
	aiOrdersInputs: { title: null, description: null, variants: null },
	aiOrdersTotalCost: 0,
	loadingAiOrdersTotalCost: false,
	// Credits
	creditsAvailable: 0,
	loadingCreditsAvailable: false,
	isCreditsTopUpOverlayOpen: false,
	// Multiple product import
	bulkAIOrders: [],
	bulkAIOrdersInputs: [], // [{ title: null, description: null, variants: null }]
	bulkAIOrdersTotalCost: 0,
	loadingBulkAIOrdersTotalCost: false,
}

export const AIStateContext = React.createContext(defaultAIState)

export const mergeAIState = (state, newValue) => {
	const nextValue =
		typeof newValue === "function" ? newValue(state) : newValue

	return {
		...state,
		...nextValue,
	}
}

export const AIStateProvider = ({ children }) => {
	const [aiState, setAIState] = React.useReducer(mergeAIState, defaultAIState)

	const getCreditsAvailable = useGetCreditsAvailable()
	const calculateTotalCreditsCostFromTokenEstimations =
		useCalculateTotalCreditsCostFromTokenEstimations()

	const debouncedAiOrders = useDebounce(aiState.aiOrders, 2500)
	const debouncedAiOrdersInputs = useDebounce(aiState.aiOrdersInputs, 2500)

	const debouncedBulkAIOrders = useDebounce(aiState.bulkAIOrders, 2500)
	const debouncedBulkAIOrdersInputs = useDebounce(
		aiState.bulkAIOrdersInputs,
		2500,
	)

	const updateCreditsAvailable = async () => {
		setAIState({ loadingCreditsAvailable: true })

		const creditsAvailable = await getCreditsAvailable({
			updateState: false,
		})

		if (creditsAvailable !== null) {
			setAIState({ creditsAvailable, loadingCreditsAvailable: false })
		} else {
			setAIState({ loadingCreditsAvailable: false })
		}
	}

	const _calculateTotalCreditsCostFromTokenEstimations = async ({
		tokenEstimations,
		context,
	}) => {
		setAIState({
			...(context === "bulk"
				? { loadingBulkAIOrdersTotalCost: true }
				: { loadingAiOrdersTotalCost: true }),
		})
		const totalEstimatedCost =
			await calculateTotalCreditsCostFromTokenEstimations({
				tokenEstimations,
			})

		if (totalEstimatedCost !== null) {
			setAIState({
				...(context === "bulk"
					? { bulkAIOrdersTotalCost: totalEstimatedCost }
					: { aiOrdersTotalCost: totalEstimatedCost }),
				...(context === "bulk"
					? { loadingBulkAIOrdersTotalCost: false }
					: { loadingAiOrdersTotalCost: false }),
			})
		} else {
			setAIState({
				...(context === "bulk"
					? { loadingBulkAIOrdersTotalCost: false }
					: { loadingAiOrdersTotalCost: false }),
			})
		}
	}

	useEffect(() => {
		updateCreditsAvailable()

		const interval = setInterval(updateCreditsAvailable, 1000 * 30)

		return () => clearInterval(interval)
	}, [])

	useEffect(() => {
		if (!debouncedAiOrders?.length) return
		loadGPTTokenizer().then(() => {
			let tokenEstimations = []

			debouncedAiOrders.map((order) => {
				const taskName = order?.task?.split(":")?.[0] || null
				const taskInputType = order?.task?.split(":")?.[1] || null

				const taskInput =
					taskInputType === "title"
						? debouncedAiOrdersInputs?.title || null
						: taskInputType === "description"
							? debouncedAiOrdersInputs?.description || null
							: taskInputType === "variants"
								? debouncedAiOrdersInputs?.variants || null
								: null

				if (taskName && taskInputType && taskInput) {
					const tokenEstimation = estimateTokens({
						taskName,
						taskInput,
						taskInputType,
						targetLang: order.targetLang || null,
					})

					if (tokenEstimation) {
						tokenEstimations.push(tokenEstimation)
					}
				}
			})

			if (tokenEstimations.length) {
				_calculateTotalCreditsCostFromTokenEstimations({
					tokenEstimations,
					context: "single",
				})
			} else {
				setAIState({
					aiOrdersTotalCost: 0,
					loadingAiOrdersTotalCost: false,
				})
			}
		})
	}, [debouncedAiOrdersInputs, debouncedAiOrders])

	useEffect(() => {
		loadGPTTokenizer().then(() => {
			let tokenEstimations = []

			if (
				debouncedBulkAIOrders?.length &&
				debouncedBulkAIOrdersInputs?.length
			) {
				debouncedBulkAIOrdersInputs.map((product) => {
					debouncedBulkAIOrders.map((order) => {
						const taskName = order?.task?.split(":")?.[0] || null
						const taskInputType =
							order?.task?.split(":")?.[1] || null

						const taskInput =
							taskInputType === "title"
								? product?.title || null
								: taskInputType === "description"
									? product?.body_html || null
									: taskInputType === "variants"
										? product?.variants?.map(
												(v) => v?.title || "",
											) || null
										: null

						if (taskName && taskInputType && taskInput) {
							const tokenEstimation = estimateTokens({
								taskName,
								taskInput,
								taskInputType,
								targetLang: order.targetLang || null,
							})

							if (tokenEstimation) {
								tokenEstimations.push(tokenEstimation)
							}
						}
					})
				})

				if (tokenEstimations.length) {
					_calculateTotalCreditsCostFromTokenEstimations({
						tokenEstimations,
						context: "bulk",
					})
				} else {
					setAIState({
						bulkAIOrdersTotalCost: 0,
						loadingBulkAIOrdersTotalCost: false,
					})
				}
			} else {
				setAIState({
					bulkAIOrdersTotalCost: 0,
					loadingBulkAIOrdersTotalCost: false,
				})
			}
		})
	}, [debouncedBulkAIOrdersInputs, debouncedBulkAIOrders])

	useEffect(() => {
		if (aiState.aiOrders?.length === 0) {
			setAIState({
				aiOrdersTotalCost: 0, // Reset total cost to avoid topup button being shown when it shouldn't
				loadingAiOrdersTotalCost: false,
			})
		} else if (aiState.aiOrders?.length > 0) {
			setAIState({ loadingAiOrdersTotalCost: true })
		}
	}, [aiState.aiOrders])

	useEffect(() => {
		if (
			aiState.bulkAIOrders?.length === 0 ||
			aiState.bulkAIOrdersInputs?.length === 0
		) {
			setAIState({
				bulkAIOrdersTotalCost: 0, // Reset total cost to avoid topup button being shown when it shouldn't
				loadingBulkAIOrdersTotalCost: false,
			})
		} else if (
			aiState.bulkAIOrders?.length > 0 &&
			aiState.bulkAIOrdersInputs?.length > 0
		) {
			setAIState({ loadingBulkAIOrdersTotalCost: true })
		}
	}, [aiState.bulkAIOrders, aiState.bulkAIOrdersInputs])

	useEffect(() => {
		if (
			aiState.aiOrders?.length > 0 &&
			(debouncedAiOrdersInputs.title !== aiState.aiOrdersInputs.title ||
				debouncedAiOrdersInputs.description !==
					aiState.aiOrdersInputs.description ||
				debouncedAiOrdersInputs.variants !==
					aiState.aiOrdersInputs.variants)
		) {
			setAIState({ loadingAiOrdersTotalCost: true })
		} else {
			setAIState({ loadingAiOrdersTotalCost: false })
		}
	}, [debouncedAiOrdersInputs, aiState.aiOrdersInputs])

	return (
		<AIStateContext.Provider value={{ aiState, setAIState }}>
			{children}
		</AIStateContext.Provider>
	)
}
